<template>
    <div
        class="flex cursor-pointer items-center gap-4 border p-4 hover:border-primary-500"
        :class="selected ? 'border-primary-800' : ''"
        :style="[
            selected
                ? 'box-shadow: 0 0 0 1px rgb(var(--color-primary-500));'
                : ''
        ]"
        @click="$emit('select')"
    >
        <radio-button
            :model-value="selected ? courseData.id : null"
            :input-id="courseData.id"
            name="course"
            :value="courseData.id"
            @update:model-value="$event ? $emit('select') : void 0"
        />

        <div class="flex-1 space-y-1">
            <div class="flex">
                <div class="flex-1">
                    <div class="text-lg font-semibold">
                        {{ courseData.publicTitle }}
                    </div>
                    <div
                        v-if="courseData.subtitle !== ''"
                        class="pb-2 text-sm"
                    >
                        {{ courseData.subtitle }}
                    </div>
                </div>
                <div
                    :class="
                        showRedMessage ? 'text-red-600 font-medium' : 'italic'
                    "
                >
                    {{ courseAvailabilityMessage }}
                </div>
            </div>
            <div class="font-medium">Sessions</div>
            <div
                class="relative"
                :class="
                    hasOverflowingContent
                        ? moreShown
                            ? ''
                            : 'max-h-[100px] overflow-hidden'
                        : ''
                "
            >
                <div ref="sessionsListDiv">
                    <ul class="list-disc pl-6">
                        <li
                            v-for="session in courseData.sessions"
                            :key="session.id"
                        >
                            {{ session.sessionSequence }} of
                            {{ session.sessionCount }} &mdash;
                            {{
                                formatRange(
                                    parseLocal(session.startDateLocal),
                                    parseLocal(session.endDateLocal)
                                )
                            }}
                            <span
                                v-if="
                                    showInstructors &&
                                    session.instructorText !== null &&
                                    session.instructorText !== ''
                                "
                                class="italic"
                                >&mdash;&nbsp;{{ session.instructorText }}</span
                            >
                            <div
                                v-if="session.subtitle !== ''"
                                class="pb-2 text-sm"
                            >
                                {{ session.subtitle }}
                            </div>
                        </li>
                    </ul>
                </div>
                <div
                    v-if="hasOverflowingContent && !moreShown"
                    class="absolute inset-x-0 -bottom-1 h-20 bg-gradient-to-b from-white/10 to-white blur-sm"
                />
            </div>
            <prime-button
                v-if="hasOverflowingContent && !moreShown"
                text
                severity="secondary"
                @click="moreShown = true"
            >
                Show More...
            </prime-button>
            <prime-button
                v-if="moreShown"
                text
                severity="secondary"
                @click="moreShown = false"
            >
                Show Less...
            </prime-button>
        </div>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const CourseDisplayCourseFragment = graphql(`
    fragment CourseDisplayCourseFragment on Course {
        id
        publicTitle
        subtitle
        capacityResult(filter: $capacityResultFilter) {
            enrollments
            overlappedBookedSessions
            contracts
        }
        sessions {
            id
            sessionSequence
            sessionCount
            startDateLocal
            endDateLocal
            instructorText
            subtitle
        }
        bookingPeriodAvailable(customerId: null, bookingDate: null)
    }
`)
</script>

<script setup lang="ts">
import RadioButton from 'primevue/radiobutton'
import { formatRange, parseLocal } from '~/composables/support/localization'
import { computed, ref } from 'vue'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import PrimeButton from 'primevue/button'
import { UNLIMITED_CAPACITY } from '~/components/PurchaseContract/Program/course-capacity'
import { useElementSize } from '@vueuse/core'

const props = defineProps<{
    course: FragmentType<typeof CourseDisplayCourseFragment>
    selected: boolean
    availabilityThreshold: number | null
    showInstructors: boolean
}>()

defineEmits<{
    (e: 'select'): void
}>()

const courseData = computed(() =>
    getFragmentData(CourseDisplayCourseFragment, props.course)
)

const courseAvailabilityMessage = computed(() => {
    // If the asset schedule isn't allowing more sessions to be booked, you can't book
    if (courseData.value.capacityResult.overlappedBookedSessions <= 0) {
        return 'Course is full.'
    }

    // If no more contracts are allowed to be booked
    if (courseData.value.capacityResult.contracts <= 0) {
        return 'Course is full.'
    }

    // If available enrollments is 0, return full
    const enrollmentCapacity = courseData.value.capacityResult.enrollments
    if (enrollmentCapacity <= 0) {
        return 'Course is full.'
    }

    // If our booking period is not available, return booking period message
    if (courseData.value.bookingPeriodAvailable === 'BEFORE_BOOKING_PERIOD') {
        return 'NOT AVAILABLE YET'
    } else if (
        courseData.value.bookingPeriodAvailable === 'AFTER_BOOKING_PERIOD'
    ) {
        return 'Booking period closed.'
    }

    // --Course Available--
    // If we have more capacity than the availability display threshold, or there is unlimited capacity
    if (
        (props.availabilityThreshold !== null &&
            enrollmentCapacity > props.availabilityThreshold) ||
        enrollmentCapacity === UNLIMITED_CAPACITY
    ) {
        return 'Available'
    }

    if (enrollmentCapacity > 1) {
        return enrollmentCapacity + ' spaces available'
    } else if (enrollmentCapacity === 1) {
        return '1 space available'
    } else {
        throw new Error()
    }
})

const showRedMessage = computed(
    () =>
        courseAvailabilityMessage.value === 'Course is full.' ||
        courseAvailabilityMessage.value === 'Booking period closed.'
)

const moreShown = ref(false)
const sessionsListDiv = ref<HTMLDivElement>()
const { height } = useElementSize(sessionsListDiv)
const hasOverflowingContent = computed(() => {
    return sessionsListDiv.value === undefined || height.value > 110
})
</script>
